import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { SubPageHeader } from "@/components/SubPageHeader";
import { Mail, MessageCircle, Download, Upload, Check, Send, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { fmtTime, fmtFullDate, todayISO, monthOf } from "@/lib/date";

export default function Backup() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [todayStatus, setTodayStatus] = useState(null);
  const [att, setAtt] = useState([]);
  const [users, setUsers] = useState([]);
  const [leaves, setLeaves] = useState([]);

  const load = async () => {
    try {
      const [t, a, u, l] = await Promise.all([
        api.get("/backup/today"),
        api.get(`/attendance?month=${monthOf()}`),
        api.get("/users"),
        api.get("/leaves"),
      ]);
      setTodayStatus(t.data); setAtt(a.data); setUsers(u.data); setLeaves(l.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const report = useMemo(() => {
    const today = todayISO();
    const lines = [];
    lines.push("📋 DAILY ATTENDANCE REPORT");
    lines.push(`📅 Date: ${fmtFullDate(today)}`);
    lines.push(`🏢 Company: ${settings?.company_name || ""}`);
    lines.push("─────────────────────");
    const todayAtt = att.filter((a) => a.date === today);
    const present = todayAtt.filter((a) => a.check_in).length;
    const total = users.length;
    lines.push(`✅ Present: ${present}/${total}`);
    lines.push(`❌ Absent: ${total - present}/${total}`);
    lines.push("─────────────────────");
    lines.push("Employee Details:");
    users.forEach((u) => {
      const a = todayAtt.find((x) => x.user_id === u.user_id);
      const onLeave = leaves.find((l) => l.user_id === u.user_id && l.date === today && l.status === "approved");
      if (a?.check_in) lines.push(`• ${u.name}: ✅ Present (${fmtTime(a.check_in)} - ${a.check_out ? fmtTime(a.check_out) : "—"})`);
      else if (onLeave) lines.push(`• ${u.name}: 🏖 On Leave`);
      else lines.push(`• ${u.name}: ❌ Absent`);
    });
    lines.push("─────────────────────");
    const month = monthOf();
    const mPresent = att.filter((a) => a.date.startsWith(month) && a.check_in && a.check_out).length;
    const mLeave = leaves.filter((l) => l.date.startsWith(month) && l.status === "approved").length;
    lines.push(`Monthly Summary (${month}):`);
    lines.push(`Present: ${mPresent} | Leave: ${mLeave}`);
    return lines.join("\n");
  }, [att, users, leaves, settings]);

  const sendEmail = async () => {
    if (!settings?.backup_email) { toast.error("Set backup email in Settings"); return; }
    const subject = `Attendance Report - ${todayISO()} - ${settings?.company_name || ""}`;
    const url = `mailto:${settings.backup_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(report)}`;
    window.location.href = url;
    if (user.role === "admin") {
      try { await api.post("/backup/sent", { channel: "email", date: todayISO() }); load(); } catch {}
    }
  };
  const sendWhatsApp = async () => {
    if (!settings?.backup_whatsapp) { toast.error("Set backup WhatsApp in Settings"); return; }
    const url = `https://wa.me/${settings.backup_whatsapp}?text=${encodeURIComponent(report)}`;
    window.open(url, "_blank");
    if (user.role === "admin") {
      try { await api.post("/backup/sent", { channel: "whatsapp", date: todayISO() }); load(); } catch {}
    }
  };

  const downloadFull = async () => {
    try {
      const { data } = await api.get("/backup/full");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `backup-${todayISO()}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed"); }
  };

  const uploadRestore = async (file) => {
    if (!file) return;
    if (!window.confirm("This will replace all data. Continue?")) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await api.post("/backup/restore", { data });
      toast.success("Restored");
    } catch { toast.error("Restore failed"); }
  };

  // ===== Daily Summary Email (admin only) =====
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [summaryLogs, setSummaryLogs] = useState([]);
  const loadSummaryLogs = async () => {
    try {
      const { data } = await api.get("/admin/daily-summary/logs?limit=5");
      setSummaryLogs(data || []);
    } catch {}
  };
  useEffect(() => { if (user?.role === "admin") loadSummaryLogs(); }, [user]);

  const sendDailySummary = async () => {
    if (summaryBusy) return;
    if (!window.confirm("Send yesterday's attendance summary email to admins & HR managers now?")) return;
    setSummaryBusy(true);
    try {
      const { data } = await api.post("/admin/daily-summary/run", {});
      const own = (data?.results || []).find((r) => r.status === "sent");
      if (own) {
        toast.success(`Sent to ${own.recipients?.join(", ") || "recipients"}`);
      } else {
        const noR = (data?.results || []).find((r) => r.status === "no_recipients");
        if (noR) toast.error("No admin/HR email set. Add an email to your admin user or set Backup Email in Settings.");
        else toast.error("Send completed with no successful deliveries — check logs.");
      }
      loadSummaryLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSummaryBusy(false);
    }
  };

  // ===== Backfill addresses (admin only) =====
  const [backfillBusy, setBackfillBusy] = useState(false);
  const backfillAddresses = async () => {
    if (backfillBusy) return;
    if (!window.confirm("Reverse-geocode all past attendance rows that have GPS but no address? This takes ~1s per unique location.")) return;
    setBackfillBusy(true);
    try {
      const { data } = await api.post("/admin/backfill-addresses", {});
      toast.success(`Scanned ${data.scanned} rows · filled ${data.check_in_filled} check-in + ${data.check_out_filled} check-out addresses`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfillBusy(false);
    }
  };

  const [middayBusy, setMiddayBusy] = useState(false);
  const sendMiddayPending = async () => {
    if (middayBusy) return;
    if (!window.confirm("Send 'Not Yet Checked-In Today' email to admins & HR managers now?")) return;
    setMiddayBusy(true);
    try {
      const { data } = await api.post("/admin/midday-pending/run", {});
      const own = (data?.results || []).find((r) => r.status === "sent");
      if (own) toast.success(`Sent to ${own.recipients?.join(", ") || "recipients"} · ${own.stats?.pending ?? 0} pending`);
      else toast.error("No admin/HR email set, or nobody pending.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setMiddayBusy(false); }
  };

  return (
    <div className="min-h-[100svh]" style={{ background: "#0A0D14", paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="backup-page">
      <SubPageHeader title="Backup & Restore" />
      <div className="px-4 py-4 space-y-3">
        <div className="rounded-2xl p-4 space-y-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
          <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Today's Auto Backup</div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Mail size={16} style={{ color: "#F75A5A" }} /><span className="text-sm" style={{ color: "#E8ECFF" }}>Email</span></div>
            <span className="text-xs font-mono" style={{ color: todayStatus?.email_sent_at ? "#22D3A5" : "#7A85A3" }}>
              {todayStatus?.email_sent_at ? <><Check size={12} className="inline" /> {fmtTime(todayStatus.email_sent_at)}</> : "Not sent"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><MessageCircle size={16} style={{ color: "#22D3A5" }} /><span className="text-sm" style={{ color: "#E8ECFF" }}>WhatsApp</span></div>
            <span className="text-xs font-mono" style={{ color: todayStatus?.whatsapp_sent_at ? "#22D3A5" : "#7A85A3" }}>
              {todayStatus?.whatsapp_sent_at ? <><Check size={12} className="inline" /> {fmtTime(todayStatus.whatsapp_sent_at)}</> : "Not sent"}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <button onClick={sendEmail} data-testid="send-email-button" className="h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5"
              style={{ background: "linear-gradient(135deg, #F75A5A, #F7C94F)", color: "#fff" }}>
              <Mail size={14} /> Email Now
            </button>
            <button onClick={sendWhatsApp} data-testid="send-whatsapp-button" className="h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5"
              style={{ background: "linear-gradient(135deg, #128C7E, #25D366)", color: "#fff" }}>
              <MessageCircle size={14} /> WhatsApp Now
            </button>
          </div>
        </div>

        <div className="rounded-2xl p-4" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
          <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "#7A85A3" }}>Report Preview</div>
          <pre className="text-[11px] font-mono whitespace-pre-wrap break-words" style={{ color: "#E8ECFF" }} data-testid="report-preview">{report}</pre>
        </div>

        {user?.role === "admin" && (
          <div className="rounded-2xl p-4 space-y-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid="daily-summary-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Daily Summary Email</div>
                <div className="text-sm mt-0.5" style={{ color: "#E8ECFF" }}>Auto-sent at <span className="font-mono">11:00 AM IST</span> daily</div>
              </div>
              <Clock size={18} style={{ color: "#22D3A5" }} />
            </div>
            <div className="text-[11px]" style={{ color: "#7A85A3" }}>
              Goes to all admin & HR Manager emails in your company. Make sure your admin user has an email set (Team → edit user).
            </div>
            <button
              onClick={sendDailySummary}
              disabled={summaryBusy}
              data-testid="send-daily-summary-now-button"
              className="w-full h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #4F8EF7, #22D3A5)", color: "#fff" }}
            >
              <Send size={14} /> {summaryBusy ? "Sending…" : "Send Yesterday\u2019s Summary Now"}
            </button>
            {summaryLogs.length > 0 && (
              <div className="pt-2 border-t" style={{ borderColor: "#1E2640" }}>
                <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "#7A85A3" }}>Recent Runs</div>
                <div className="space-y-1 max-h-32 overflow-auto" data-testid="daily-summary-logs">
                  {summaryLogs.map((log, i) => {
                    const r = (log.results || [])[0] || {};
                    return (
                      <div key={i} className="flex items-center justify-between text-[11px]" style={{ color: "#E8ECFF" }}>
                        <span className="font-mono" style={{ color: "#7A85A3" }}>{(log.ran_at || "").slice(0, 16).replace("T", " ")}</span>
                        <span style={{ color: r.status === "sent" ? "#22D3A5" : "#F75A5A" }}>{r.status || "—"}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {user?.role === "admin" && (
          <div className="rounded-2xl p-4 space-y-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid="midday-pending-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Midday Not-Checked-In Alert</div>
                <div className="text-sm mt-0.5" style={{ color: "#E8ECFF" }}>Auto-sent at <span className="font-mono">1:00 PM IST</span> daily</div>
              </div>
              <Clock size={18} style={{ color: "#fb923c" }} />
            </div>
            <div className="text-[11px]" style={{ color: "#7A85A3" }}>
              Lists every employee who hasn&rsquo;t checked in today (and isn&rsquo;t on approved leave) so you can call them. Same recipients as the 11&nbsp;AM summary.
            </div>
            <button
              onClick={sendMiddayPending}
              disabled={middayBusy}
              data-testid="send-midday-now-button"
              className="w-full h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #fb923c, #f97316)", color: "#fff" }}
            >
              <Send size={14} /> {middayBusy ? "Sending…" : "Send Today\u2019s Pending Check-Ins Now"}
            </button>
          </div>
        )}

        {user?.role === "admin" && (
          <div className="rounded-2xl p-4 space-y-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid="backfill-addresses-card">
            <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Street Addresses</div>
            <div className="text-[11px]" style={{ color: "#7A85A3" }}>
              New check-ins auto-resolve to a street address. Use this to backfill old rows that only have GPS coordinates.
            </div>
            <button
              onClick={backfillAddresses}
              disabled={backfillBusy}
              data-testid="backfill-addresses-btn"
              className="w-full h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #9B6FEB, #4F8EF7)", color: "#fff" }}
            >
              <MapPin size={14} /> {backfillBusy ? "Resolving…" : "Backfill Addresses for Old Rows"}
            </button>
          </div>
        )}

        {user?.role === "admin" && (
          <div className="rounded-2xl p-4 space-y-2" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
            <div className="text-[11px] uppercase tracking-widest mb-1" style={{ color: "#7A85A3" }}>Full Data</div>
            <button onClick={downloadFull} data-testid="download-backup" className="w-full h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2" style={{ background: "#4F8EF7", color: "#fff" }}>
              <Download size={14} /> Download Full Backup (JSON)
            </button>
            <label className="w-full h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2 cursor-pointer" style={{ background: "transparent", color: "#F7C94F", border: "1px solid rgba(247,201,79,0.4)" }}>
              <Upload size={14} /> Upload & Restore
              <input type="file" accept="application/json" className="hidden" onChange={(e) => uploadRestore(e.target.files?.[0])} data-testid="upload-restore" />
            </label>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-2xl p-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
            <div className="font-syne text-2xl font-bold" style={{ color: "#E8ECFF" }}>{users.length}</div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Employees</div>
          </div>
          <div className="rounded-2xl p-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
            <div className="font-syne text-2xl font-bold" style={{ color: "#E8ECFF" }}>{att.length}</div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Attendance (mo)</div>
          </div>
        </div>
      </div>
    </div>
  );
}
